import { useState } from 'react';
import {
  Badge,
  Button,
  Countdown,
  EmptyState,
  Panel,
  ResourceIcon,
  Stat,
  StatGrid,
  Tabs,
  TradeIcon,
  ViewHeader,
  formatAmount,
  formatExact,
  formatPercent,
} from '@spacegame/ui';
import {
  MARKET_RATES,
  RESOURCE_LABELS,
  TRADEABLE,
  buyPrice,
  effectiveTax,
  marketUnlocked,
  sellValue,
} from '@spacegame/engine';
import { useGame, useGameActions } from '../game/GameProvider';

/** Preset trade sizes — typing exact amounts is friction nobody wants. */
const STEPS = [1_000, 10_000, 100_000];

export function TradeView() {
  const { game } = useGame();
  const actions = useGameActions();
  const [mode, setMode] = useState<'sell' | 'buy'>('sell');

  if (!marketUnlocked(game)) {
    return (
      <>
        <ViewHeader eyebrow="Gesperrt" title="Händler-Portal" />
        <Panel>
          <EmptyState
            icon={<TradeIcon size={26} />}
            title="Händler-Portal nicht verfügbar"
            description="Errichte eine Händler-Relaisstation (Stufe 1), um mit NPC-Händlern zu handeln und Angebote zu erhalten."
          />
        </Panel>
      </>
    );
  }

  const tax = effectiveTax(game);

  return (
    <>
      <ViewHeader
        eyebrow={`Relaisstation Stufe ${game.planets[0]!.buildings.tradeRelay}`}
        title="Händler-Portal"
        description="Rohstoffe gegen Galaktische Credits handeln. Die Marktsteuer sinkt mit Logistik-Forschung und dem Wirtschafts-Talentpfad."
        actions={
          <Tabs
            variant="enclosed"
            value={mode}
            onChange={(id) => setMode(id as 'sell' | 'buy')}
            items={[
              { id: 'sell', label: 'Verkaufen' },
              { id: 'buy', label: 'Kaufen' },
            ]}
          />
        }
      />

      <StatGrid columns={3}>
        <Stat label="Marktsteuer" value={formatPercent(tax, 1)} accent="credit" hint="Basis 15 % · Minimum 3 %" />
        <Stat label="Credits" value={formatAmount(game.resources.credit)} accent="credit" />
        <Stat
          label="Logistik-Stufe"
          value={String(game.research.logistics)}
          accent="silicate"
          hint="−1 % Steuer pro Stufe"
        />
      </StatGrid>

      <Panel title={mode === 'sell' ? 'Rohstoffe verkaufen' : 'Rohstoffe kaufen'} corners>
        <div className="space-y-2">
          {TRADEABLE.map((resource) => (
            <div
              key={resource}
              className="flex flex-wrap items-center gap-3 rounded-control border border-edge-subtle bg-surface-2 px-3 py-2.5"
            >
              <ResourceIcon resource={resource} size={24} />
              <div className="min-w-0 flex-1">
                <div className="font-display text-xs font-medium uppercase tracking-[0.08em] text-content">
                  {RESOURCE_LABELS[resource]}
                </div>
                <div className="text-label text-content-muted" data-sg-numeric>
                  Bestand: {formatExact(Math.floor(game.resources[resource]))} ·
                  {' '}Kurs {MARKET_RATES[resource]} Cr/Einheit
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5">
                {STEPS.map((amount) => {
                  const price =
                    mode === 'sell'
                      ? sellValue(game, resource, amount)
                      : buyPrice(game, resource, amount);
                  const affordable =
                    mode === 'sell'
                      ? game.resources[resource] >= amount
                      : game.resources.credit >= price;
                  return (
                    <Button
                      key={amount}
                      size="sm"
                      variant={mode === 'sell' ? 'secondary' : 'primary'}
                      disabled={!affordable || price <= 0}
                      onClick={() =>
                        mode === 'sell'
                          ? actions.sellResource(resource, amount)
                          : actions.buyResource(resource, amount)
                      }
                      title={
                        mode === 'sell'
                          ? `${formatExact(amount)} verkaufen für ${formatExact(price)} Credits`
                          : `${formatExact(amount)} kaufen für ${formatExact(price)} Credits`
                      }
                    >
                      {formatAmount(amount, { threshold: 1000 })}
                      <span className="ml-1 opacity-70">
                        {mode === 'sell' ? '+' : '−'}
                        {formatAmount(price, { threshold: 1000 })}
                      </span>
                    </Button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </Panel>

      <Panel
        title="Aktuelle Angebote"
        eyebrow="Rotiert alle 4 Stunden"
        actions={
          game.market.offers.length > 0 ? (
            <span className="text-label text-content-muted">
              Nächste Rotation in <Countdown target={game.market.refreshAt} format="short" />
            </span>
          ) : undefined
        }
      >
        {game.market.offers.length === 0 ? (
          <EmptyState
            icon={<TradeIcon size={24} />}
            title="Keine Angebote"
            description="Die Handelsrouten werden neu berechnet. Schau in Kürze wieder vorbei."
          />
        ) : (
          <div className="grid gap-2 sm:grid-cols-3">
            {game.market.offers.map((offer) => {
              const affordable = game.resources.credit >= offer.priceCredits;
              return (
                <div
                  key={offer.id}
                  className="flex flex-col gap-2 rounded-control border border-edge-subtle bg-surface-2 p-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <ResourceIcon resource={offer.resource} size={20} />
                    <Badge accent="credit" size="sm">
                      −{formatPercent(offer.discount)}
                    </Badge>
                  </div>
                  <div>
                    <div data-sg-numeric className="text-readout font-semibold text-content">
                      {formatAmount(offer.amount)}
                    </div>
                    <div className="text-label text-content-muted">{RESOURCE_LABELS[offer.resource]}</div>
                  </div>
                  <div data-sg-numeric className="text-xs text-credit-bright">
                    {formatExact(offer.priceCredits)} Credits
                  </div>
                  <Button
                    size="sm"
                    variant="primary"
                    fullWidth
                    disabled={!affordable}
                    onClick={() => actions.acceptOffer(offer.id)}
                  >
                    {affordable ? 'Kaufen' : 'Zu teuer'}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </Panel>
    </>
  );
}
